import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Upload, Trash2, Copy, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const MediaManager = () => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/media`);
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch media files',
        variant: 'destructive'
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({
        title: 'Success',
        description: 'File uploaded successfully'
      });
      
      fetchMedia();
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/media/${mediaId}`);
      toast({
        title: 'Success',
        description: 'File deleted successfully'
      });
      fetchMedia();
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive'
      });
    }
  };

  const copyToClipboard = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast({
      title: 'Copied',
      description: 'URL copied to clipboard'
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-4">Media Library</h2>
        
        {/* Upload Section */}
        <div className="mb-8">
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-pink-500 transition-colors">
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">
                {uploading ? 'Uploading...' : 'Click to upload files'}
              </p>
              <p className="text-sm text-gray-500">Images and videos supported</p>
            </div>
            <input
              id="file-upload"
              type="file"
              accept="image/*,video/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>

        {/* Media Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {media.map((item) => (
            <div key={item.id} className="group relative bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              {item.type === 'image' ? (
                <img src={item.url} alt={item.filename} className="w-full h-48 object-cover" />
              ) : (
                <video src={item.url} className="w-full h-48 object-cover" />
              )}
              
              <div className="p-4">
                <p className="text-sm font-medium truncate mb-2">{item.filename}</p>
                <p className="text-xs text-gray-500 mb-3">
                  {(item.size / 1024).toFixed(2)} KB
                </p>
                
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => copyToClipboard(item.url, item.id)}
                  >
                    {copiedId === item.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {media.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No media files uploaded yet
          </div>
        )}
      </Card>
    </div>
  );
};

export default MediaManager;
